import { Navigate, useNavigate } from 'react-router';
import { useLocation, useSearchParams, Outlet } from 'react-router';
import { validate as isUuid } from 'uuid';

import Loader from './Loader';
import MyLandbot from '../components/Landbot';
import PageWrapper from './PageWrapper/V1';
import { Userpilot } from 'userpilot';
import { cachedSessionStorage } from '../utils/cachedSessionStorage';
import {
  shouldUserStayInEnrollProperty,
  userShouldBeIdentifiedForUserPilot,
} from '../utils/commonUtils';
import getEnv from '../env';
import getUser from '../utils/apiRestCalls/getUser';
import { routes } from '../constants/Constants';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUrlChecker } from '../hooks/useUrlChecker';
import { usePermission } from '@/hooks/usePermission';
import { UserPermissions } from '@/constants/permissions.constant';
import { datadogRum } from '@datadog/browser-rum';
import useABTestStore from '@/stores/abTestStore';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import { trackOnClick } from '@/utils/trackOnClick';
import { PublicRoutes } from '@/routes/PublicRoutes';
import { PrivateRoutes } from '@/routes/PrivateRoutes';
import PageWrapperV2 from './PageWrapper/V2';
import type { User } from '../utils/types/User';
import { TRANSUNION_REFERRAL_ID } from '@/features/Registration/hooks/useReferralId';
import { isValidAlphaID, ALPHA_ID_PREFIXES } from '@theguarantors/tg-modern-business';

/**
 * Checks for the property enrolment form URL including hash handles.
 * This helps prevent redirection loops/bugs by avoiding (eg.) the `#profile` handle
 * getting lost during redirect.
 */
const getEnrollFormRedirectTarget = (currentPathname: string) => {
  return currentPathname.match(new RegExp('\/enroll\/property-(form|enroll)(#.*$|$)'))
    ? currentPathname
    : routes.propertyEnrollmentForm;
};

/**
 * Determines if a new self-service user should be redirected to the first property enrollment page.
 * Returns true if:
 * - User is a new self-service user who can enroll
 * - AND either: not on enrollment page, OR on enrollment page but missing isFirstProperty state
 *
 * The above is necessary to ensure we're rendering the correct title
 * ("Add your first property" / "Add a new property")
 */
export const shouldRedirectToFirstPropertyEnroll = (
  pathname: string,
  user: User | undefined,
  canEnrollProperties: boolean,
  hasFirstPropertyState: boolean,
) => {
  const isNewUserWhoCanEnroll =
    canEnrollProperties && shouldUserStayInEnrollProperty(pathname, user);

  if (!isNewUserWhoCanEnroll) return false;

  const isOnEnrollmentPage = getEnrollFormRedirectTarget(pathname) === pathname;

  if (isOnEnrollmentPage && hasFirstPropertyState) {
    return false;
  }
  return true;
};

export default function SiteWrapper() {
  const [searchParams] = useSearchParams();
  const locationPath = useLocation();
  const navigate = useNavigate();
  const { isPublic } = useUrlChecker();
  const { notSet: canEnrollPropertiesNotSet, enabled: canEnrollPropertiesEnabled } = usePermission(
    UserPermissions.Properties.CanEnrollProperty,
  );
  const canEnrollProperties = canEnrollPropertiesNotSet || canEnrollPropertiesEnabled;
  const { loaded: abTestStoreLoaded } = useABTestStore();
  useABTestStore.getState().useFetchABTestsValues();
  const { page, track, identify, isReady: analyticsIsReady } = useAnalyticsContext();

  const {
    data: user,
    isLoading,
    status,
    error,
  } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUser(),
    staleTime: Infinity,
    enabled: !isPublic,
  });

  const hasFirstPropertyState = locationPath.state?.isFirstProperty === true;

  const shouldRedirectToEnroll = shouldRedirectToFirstPropertyEnroll(
    locationPath.pathname,
    user,
    canEnrollProperties,
    hasFirstPropertyState,
  );

  const needsRedirect = !isPublic && user && (user.role !== 'landlord' || shouldRedirectToEnroll);

  const redirectEvaluated =
    isPublic || status === 'error' || (status === 'success' && !needsRedirect);

  useEffect(() => {
    cachedSessionStorage.cacheStorage();
    // Take all query params for the future.
    cachedSessionStorage.storeUTM(searchParams);
  }, [searchParams]);

  useEffect(() => {
    if (!analyticsIsReady || !abTestStoreLoaded) {
      return;
    }

    window.scrollTo({ top: 0, behavior: 'instant' });

    if (!locationPath.pathname || locationPath.pathname === routes.home) return;
    page(locationPath.pathname + (locationPath.hash ? `${locationPath.hash}` : ''));

    const oldTitle = document.title;
    const cleanedUrlPath = [...PublicRoutes, ...PrivateRoutes].find((route) => {
      // Exact match for static routes
      if (route.path === locationPath.pathname) {
        return true;
      }

      // Special handling for /users/:userId - check if it's a UUID
      if (route.path === routes.userDetails) {
        const pathSegments = locationPath.pathname.split('/');
        const userId = pathSegments[2]; // Extract userId from /users/:userId
        return userId && isUuid(userId);
      }

      // Special handling for /claims/:claimAlphaId/submissions/:claimSubmissionAlphaId - check both alpha ids
      if (route.path === routes.claimsSubmissionDetails) {
        const pathSegments = locationPath.pathname.split('/');
        const claimAlphaId = pathSegments[2];
        const claimSubmissionAlphaId = pathSegments[4];
        return (
          claimAlphaId &&
          isValidAlphaID(ALPHA_ID_PREFIXES.CLAIM, claimAlphaId) &&
          claimSubmissionAlphaId &&
          isValidAlphaID(ALPHA_ID_PREFIXES.CLAIM_SUBMISSION, claimSubmissionAlphaId)
        );
      }

      return false;
    });

    // Preserve the title if the route doesn't match.
    const newTitle = cleanedUrlPath?.title;
    if (!newTitle || oldTitle === newTitle) return;
    document.title = newTitle;
  }, [analyticsIsReady, abTestStoreLoaded, page, locationPath.pathname, locationPath.hash]);

  useEffect(() => {
    if (status !== 'success') {
      return;
    }

    if (analyticsIsReady) {
      identify(user);
    }

    if (userShouldBeIdentifiedForUserPilot(user.email)) {
      Userpilot.identify(user.id ?? '', {
        name: user.firstName ?? '',
        email: user.email ?? '',
        LLType: user.LLType,
        canEnrollProperties: user.canEnrollProperties,
        referralId: user.partner ?? '',
        env: getEnv().app_env,
      });
    }

    datadogRum.setUser({
      id: user.id ?? '',
      email: user.email ?? '',
      name: `${user.firstName} ${user.lastName}`,
      permissions: user.userPermissions,
      LLType: user.LLType,
      env: getEnv().app_env,
    });

    if (user?.role !== 'landlord') {
      navigate(routes.limitation, { replace: true });
      return;
    }

    if (shouldRedirectToEnroll) {
      cachedSessionStorage.setItem('isUserFirstTimeEnrolling', 'true');
      navigate(`${routes.propertyEnrollmentForm}${locationPath.search}${locationPath.hash}`, {
        replace: true,
        state: { isFirstProperty: true },
      });
    }
  }, [
    navigate,
    status,
    user,
    shouldRedirectToEnroll,
    identify,
    analyticsIsReady,
    locationPath.search,
    locationPath.hash,
  ]);

  useEffect(() => {
    if (user) {
      Userpilot.reload();
    }
  }, [locationPath.pathname, user]);

  if (error) return <Navigate to={routes.error500} replace={true} />;
  if (!isPublic && (isLoading || !redirectEvaluated)) return <Loader />;

  return (
    <div
      id="shell_container"
      role="button"
      tabIndex={0}
      onClick={(event) => trackOnClick(event, track)}
    >
      {user &&
        canEnrollProperties &&
        user.partner?.toLowerCase() === TRANSUNION_REFERRAL_ID.toLowerCase() && <MyLandbot />}
      {isPublic ? (
        <PageWrapper>
          <Outlet />
        </PageWrapper>
      ) : (
        <PageWrapperV2>
          <Outlet />
        </PageWrapperV2>
      )}
    </div>
  );
}
